
import { supabase } from '@/integrations/supabase/client';
import { MonthlyMetric } from './monthlyMetricsService';

/**
 * Получает активность парка по месяцам - процент велосипедов, которые использовались
 */
export const getMonthlyFleetActivity = async (monthsCount: number = 12): Promise<MonthlyMetric[]> => {
  try {
    console.log(`Загрузка активности парка за ${monthsCount} месяцев...`);
    
    // Используем прямой расчет без RPC функции
    return await getMonthlyFleetActivityFallback(monthsCount);
  } catch (error) {
    console.error('Ошибка при расчете активности парка:', error);
    return [];
  }
};

/**
 * Расчет активности парка без RPC - основной метод
 */
const getMonthlyFleetActivityFallback = async (monthsCount: number): Promise<MonthlyMetric[]> => {
  try {
    console.log('Рассчитываем активность парка...');
    
    const endDate = new Date();
    const startDate = new Date(Date.now() - monthsCount * 30 * 24 * 60 * 60 * 1000);
    
    // Получаем все велосипеды с учетом активных статусов
    const { data: bikesData, error: bikesError } = await supabase
      .from('bikes')
      .select('id, created_at, status')
      .order('created_at', { ascending: true });

    if (bikesError) {
      console.error('Ошибка при получении велосипедов:', bikesError);
      return [];
    }

    // Получаем ВСЕ аренды с привязанными велосипедами (независимо от статуса платежа)
    const { data: rentalsData, error: rentalsError } = await supabase
      .from('rentals')
      .select('bike_id, start_date, end_date, status, created_at')
      .not('bike_id', 'is', null)
      .order('start_date', { ascending: true });

    if (rentalsError) {
      console.error('Ошибка при получении аренд:', rentalsError);
      return [];
    }

    const bikes = bikesData || [];
    const rentals = rentalsData || [];

    console.log(`Всего велосипедов: ${bikes.length}, всех аренд с велосипедами: ${rentals.length}`);

    // Получаем все уникальные статусы для отладки
    const allBikeStatuses = [...new Set(bikes.map(bike => bike.status))];
    const allRentalStatuses = [...new Set(rentals.map(rental => rental.status))];
    console.log('Все статусы велосипедов в БД:', allBikeStatuses);
    console.log('Все статусы аренд в БД:', allRentalStatuses);

    const metrics: MonthlyMetric[] = [];
    
    // Активные статусы велосипедов (исключаем только списанные и утерянные)
    const activeStatuses = ['available', 'rented', 'in_use', 'maintenance', 'blocked', 'active', 'inactive'];
    console.log('Считаем активными статусы велосипедов:', activeStatuses);
    
    // Генерируем месяцы для анализа
    for (let i = 0; i < monthsCount; i++) {
      const monthDate = new Date(endDate.getFullYear(), endDate.getMonth() - i, 1);
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth() + 1;
      
      // Определяем границы месяца
      const monthStart = new Date(year, month - 1, 1);
      const monthEnd = new Date(year, month, 0, 23, 59, 59, 999);
      
      // Считаем активные велосипеды на конец месяца
      const activeBikesInMonth = bikes.filter(bike => {
        const bikeCreated = new Date(bike.created_at);
        const isCreatedBeforeMonthEnd = bikeCreated <= monthEnd;
        const isActiveStatus = activeStatuses.includes(bike.status || 'available');
        return isCreatedBeforeMonthEnd && isActiveStatus;
      }).length;
      
      // Находим уникальные велосипеды с арендами, которые ПЕРЕСЕКАЮТСЯ с этим месяцем
      const uniqueRentedBikes = new Set<string>();
      
      rentals.forEach(rental => {
        if (!rental.bike_id || !rental.start_date) return;
        
        const rentalStart = new Date(rental.start_date);
        // Если end_date не указана, считаем аренду активной (до конца месяца)
        const rentalEnd = rental.end_date ? new Date(rental.end_date) : monthEnd;
        
        // Проверяем пересечение периода аренды с месяцем
        const hasIntersection = rentalStart <= monthEnd && rentalEnd >= monthStart;
        
        if (hasIntersection) {
          uniqueRentedBikes.add(rental.bike_id);
        }
      });
      
      // Рассчитываем активность парка с валидацией
      let fleetActivity = 0;
      if (activeBikesInMonth > 0) {
        const rawActivity = (uniqueRentedBikes.size / activeBikesInMonth) * 100;
        
        // Валидация: максимум 100%
        if (rawActivity > 100) {
          console.warn(`Месяц ${year}-${String(month).padStart(2, '0')}: расчетная активность ${rawActivity.toFixed(1)}% превышает 100%. Используется 100%.`);
          fleetActivity = 100;
        } else {
          fleetActivity = rawActivity;
        }
      }
      
      console.log(`Месяц ${year}-${String(month).padStart(2, '0')}: активных велосипедов ${activeBikesInMonth}, использовано уникальных ${uniqueRentedBikes.size}, активность ${fleetActivity.toFixed(1)}%`);
      
      // Отладочная информация о пересечениях
      if (uniqueRentedBikes.size > 0) {
        const intersectingRentals = rentals.filter(rental => {
          if (!rental.bike_id || !rental.start_date) return false;
          const rentalStart = new Date(rental.start_date);
          const rentalEnd = rental.end_date ? new Date(rental.end_date) : monthEnd;
          return rentalStart <= monthEnd && rentalEnd >= monthStart;
        });
        console.log(`  - Найдено ${intersectingRentals.length} аренд, пересекающихся с месяцем`);
      }
      
      metrics.unshift({
        year,
        month,
        value: Math.round(fleetActivity * 10) / 10 // Округляем до 1 знака после запятой
      });
    }

    // Рассчитываем рост по сравнению с предыдущим месяцем
    for (let i = 1; i < metrics.length; i++) {
      const current = metrics[i].value;
      const previous = metrics[i - 1].value;
      metrics[i].previousValue = previous;
      if (previous > 0) {
        metrics[i].growth = Math.round(((current - previous) / previous) * 100);
      }
    }

    console.log(`Рассчитана активность парка за ${metrics.length} месяцев`);
    return metrics;
  } catch (error) {
    console.error('Ошибка при расчете активности парка:', error);
    return [];
  }
};
